
import Vue, { PropType } from 'vue';
import { CvGrid, CvRow, CvColumn, CvButton, CvButtonSet } from '@carbon/vue';
import { SmallDetailsResult, SmallDetailsPayload } from '@/typings';
import Close16 from '@carbon/icons-vue/es/close/16';
import CheckmarkFilled16 from '@carbon/icons-vue/es/checkmark--filled/16';
import CloseFilled16 from '@carbon/icons-vue/es/close--filled/16';
import WarningFilled16 from '@carbon/icons-vue/es/warning--filled/16';
import InformationFilled16 from '@carbon/icons-vue/es/information--filled/16';
import { base64StringToBlob } from 'blob-util';
import { getSmallDetailsStatus } from '../index';
import {
  magnify,
  removeMagnify,
} from '@/components/SmallDetails/utils/magnify';
import ZoomArea24 from '@carbon/icons-vue/es/zoom--area/24';
import AddAlt24 from '@carbon/icons-vue/es/add--alt/24';
import SubtractAlt24 from '@carbon/icons-vue/es/subtract--alt/24';
import { SmallDetailsStatus } from '@midocean/webapps-shared-domain';

export default Vue.extend({
  name: 'SmallDetailsOneLogo',
  components: {
    CvGrid,
    CvColumn,
    CvRow,
    Close16,
    CheckmarkFilled16,
    WarningFilled16,
    InformationFilled16,
    CloseFilled16,
    CvButton,
    CvButtonSet,
    ZoomArea24,
    AddAlt24,
    SubtractAlt24,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
    data: {
      type: Object as PropType<SmallDetailsResult>,
      required: true,
    },
    pngFile: {
      type: Blob,
      default: undefined,
      required: false,
    },
    fileName: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      activeIdx: 1,
      isZoomEnabled: false,
      zoomSize: 2,
    };
  },

  computed: {
    status(): SmallDetailsStatus {
      return getSmallDetailsStatus(
        this.data.positiveDetailsLoss,
        this.data.negativeDetailsLoss,
        this.data.calculatedQuality
      );
    },
    uploadButtonLabel(): string {
      if (this.status === 'Bad') {
        return this.$t('section.actions.upload-better-logo').toString();
      }
      if (this.status === 'Average') {
        return this.$t('section.actions.upload-a-good-logo').toString();
      }

      if (this.status === 'NoCheck') {
        return this.$t('section.actions.proceed-without-check').toString();
      }
      return this.$t('section.actions.import').toString();
    },
    proceedButtonLabel(): string {
      if (this.status === 'Bad') {
        return this.$t('section.actions.proceed-with-this-bad-logo').toString();
      }
      if (this.status === 'Average') {
        return this.$t('section.actions.proceed-with-this-logo').toString();
      }
      if (this.status === 'NoCheck') {
        return this.$t('section.actions.upload-new-logo').toString();
      }
      return this.$t('section.actions.back-to-library').toString();
    },
    imageUrl(): string {
      return this.status !== 'NoCheck'
        ? URL.createObjectURL(
            base64StringToBlob(this.data.pngFile, 'image/png')
          )
        : URL.createObjectURL(this.pngFile as Blob);
    },
    smallDetailsPayload(): SmallDetailsPayload {
      return {
        negativeDetailsLoss: this.data.negativeDetailsLoss,
        positiveDetailsLoss: this.data.positiveDetailsLoss,
        pngFileBlob: this.data.pngFile
          ? base64StringToBlob(this.data.pngFile, 'image/png')
          : new Blob(),
        status: this.status,
        calculatedQuality: this.data.calculatedQuality,
        pngFile: this.data.pngFile ? this.data.pngFile : '',
        isCheckValid: true,
      } as SmallDetailsPayload;
    },
  },

  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.$modal.show('small-details-one-logo-modal');
      } else {
        this.$modal.hide('small-details-one-logo-modal');
      }
    },
    activeIdx() {
      // reset magnify glass
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      const element =
        subtarget && subtarget.shadowRoot
          ? subtarget.shadowRoot.getElementById('magnifier-glass')
          : document.getElementById('magnifier-glass');

      element && element.remove();
      magnify('myimage', this.zoomSize, 'w2p-import-wizard');
    },
    zoomSize() {
      removeMagnify('w2p-import-wizard');
      magnify('myimage', this.zoomSize, 'w2p-import-wizard');
    },
  },

  mounted() {
    if (this.isOpen) {
      this.$modal.show('small-details-one-logo-modal');
    }
  },
  methods: {
    onProceed() {
      if (this.status === 'Good') {
        this.$emit('action:back-to-library');
      }

      if (this.status === 'Average' || this.status === 'Bad') {
        this.smallDetailsPayload.approvedAt = new Date().toISOString();
        this.$emit('action:proceed-with-logo', this.smallDetailsPayload);
      }

      if (this.status === 'NoCheck') {
        this.$emit('action:upload-new-logo');
      }

      this.$modal.hide('small-details-one-logo-modal');
    },
    onUpload() {
      if (this.status === 'Good' || this.status === 'NoCheck') {
        this.$emit('action:proceed-with-logo', this.smallDetailsPayload);
      }

      if (this.status === 'Average' || this.status === 'Bad') {
        this.$emit('action:upload-new-logo');
      }

      this.$modal.hide('small-details-one-logo-modal');
    },
    enableZoom() {
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      const element =
        subtarget && subtarget.shadowRoot
          ? subtarget.shadowRoot.getElementById('magnifier-glass')
          : document.getElementById('magnifier-glass');
      if (element) {
        this.isZoomEnabled = false;
        this.zoomSize = 2;
        removeMagnify('w2p-import-wizard');
      } else {
        this.isZoomEnabled = true;
        magnify('myimage', this.zoomSize, 'w2p-import-wizard');
      }
    },
  },
});
